import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, PlusCircle, Eye, Edit, Trash2, Languages, CheckCircle, Lock } from 'lucide-react';
import API_BASE from './apiBase';
import { normalizeConsentKey, buildMultilingualVariantPayload } from './multiLanguageConsentUtils';
import Swal from 'sweetalert2';

const styles = {
  container: { minHeight: '100vh', background: '#f8fafc', padding: '1.5rem', boxSizing: 'border-box' },
  header: { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: '#fff', borderRadius: '24px', padding: '1.25rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap', boxSizing: 'border-box' },
  title: { margin: 0, fontSize: '1.35rem', fontWeight: 700, wordBreak: 'break-word' },
  subTitle: { margin: '0.35rem 0 0', color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem' },
  btn: { border: 'none', borderRadius: '12px', padding: '0.7rem 1rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
  primaryBtn: { background: '#fff', color: '#2563eb' },
  secondaryBtn: { background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.25rem', width: '100%', boxSizing: 'border-box' },
  card: { background: '#fff', borderRadius: '20px', padding: '1.1rem', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)', border: '1px solid #e2e8f0', position: 'relative', display: 'flex', flexDirection: 'column', minHeight: '240px', transition: 'transform 0.2s ease, box-shadow 0.2s ease', overflow: 'hidden', boxSizing: 'border-box', width: '100%' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.7rem', width: '100%', boxSizing: 'border-box' },
  cardTitle: { fontWeight: 700, color: '#0f172a', margin: '0.35rem 0 0', fontSize: '1rem', lineHeight: 1.3, wordBreak: 'break-word', overflowWrap: 'anywhere' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.7rem', borderRadius: '999px', background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', color: '#6d28d9', fontSize: '0.8rem', fontWeight: 700, boxShadow: 'inset 0 0 0 1px rgba(109, 40, 217, 0.1)', maxWidth: '100%', boxSizing: 'border-box' },
  cardMeta: { fontSize: '0.83rem', color: '#64748b', marginBottom: '0.6rem', wordBreak: 'break-word' },
  contentPreview: { color: '#334155', fontSize: '0.93rem', whiteSpace: 'pre-wrap', marginBottom: '0.7rem', lineHeight: 1.6, flex: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' },
  iconBtnRow: { display: 'flex', gap: '0.4rem', flexShrink: 0, alignItems: 'center' },
  iconBtn: { border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', borderRadius: '10px', width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 },
  iconBtnView: { color: '#2563eb' },
  iconBtnEdit: { color: '#2563eb' },
  iconBtnDelete: { color: '#dc2626', borderColor: '#fecaca', background: '#fef2f2' },
  empty: { background: '#fff', padding: '1.25rem', borderRadius: '12px', color: '#64748b', border: '1px dashed #cbd5e1' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400, padding: '1rem', boxSizing: 'border-box', overflowY: 'auto', overscrollBehavior: 'contain' },
  modal: { background: '#fff', width: '100%', maxWidth: '560px', borderRadius: '16px', padding: '1rem', boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)', boxSizing: 'border-box' },
  viewModal: { background: '#fff', width: '100%', maxWidth: '560px', maxHeight: 'calc(100vh - 2rem)', borderRadius: '16px', padding: '1rem', boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  viewContent: { minHeight: 0, flex: '1 1 auto', overflowY: 'auto', overscrollBehavior: 'contain', paddingRight: '0.25rem' },
  input: { width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1', marginBottom: '0.8rem', boxSizing: 'border-box' },
  textarea: { width: '100%', minHeight: '120px', padding: '0.75rem', borderRadius: '10px', border: '1px solid #cbd5e1', resize: 'vertical', boxSizing: 'border-box' },
  viewLabel: { fontSize: '0.8rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.25rem' },
  viewValue: { fontSize: '0.95rem', color: '#0f172a', marginBottom: '1rem', whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' },
};

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "as", label: "Assamese" },
  { value: "bn", label: "Bengali" },
  { value: "bodo", label: "Bodo" },
  { value: "doi", label: "Dogri" },
  { value: "gu", label: "Gujarati" },
  { value: "hi", label: "Hindi" },
  { value: "kn", label: "Kannada" },
  { value: "ks", label: "Kashmiri" },
  { value: "kok", label: "Konkani" },
  { value: "mai", label: "Maithili" },
  { value: "ml", label: "Malayalam" },
  { value: "mni", label: "Manipuri (Meitei)" },
  { value: "mr", label: "Marathi" },
  { value: "ne", label: "Nepali" },
  { value: "or", label: "Odia" },
  { value: "pa", label: "Punjabi" },
  { value: "sa", label: "Sanskrit" },
  { value: "sat", label: "Santali" },
  { value: "sd", label: "Sindhi" },
  { value: "ta", label: "Tamil" },
  { value: "te", label: "Telugu" },
  { value: "ur", label: "Urdu" },
];

function SubMultilingualConsentPage({ parentConsent, tenantId, token, onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [title, setTitle] = useState('');
  const [languageCode, setLanguageCode] = useState('hi');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const parentKey = useMemo(() => normalizeConsentKey(parentConsent?.key || parentConsent?.consent_type || parentConsent?.name || ''), [parentConsent]);
  const parentLabel = parentConsent?.label || parentConsent?.consent_type || parentConsent?.name || 'Consent';
  const parentSubConsentId = parentConsent?.id ?? parentConsent?.raw?.id ?? null;
  const parentConsentId = parentConsent?.parent_consent_id ?? parentConsent?.consent_id ?? parentConsent?.raw?.parent_consent_id ?? parentConsent?.raw?.consent_id ?? null;
  const parentSubConsentName = parentConsent?.sub_category || parentConsent?.parsed?.sub_consent_name || parentConsent?.parsed?.title || '';
  const parentParsed = parentConsent?.parsed || parentConsent?.raw?.parsed || {};
  const parentVersion = String(parentConsent?.version || parentConsent?.consent_version || parentParsed.version || parentParsed.consent_version || '1.0.0');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (parentSubConsentId == null) {
        setRows([]);
        setError('This sub-consent has no stable ID. Refresh the sub-consent list before adding language versions.');
        setLoading(false);
        return;
      }
      const parentQuery = `&parent_sub_consent_id=${encodeURIComponent(parentSubConsentId)}`;
      const url = `${API_BASE}/manager/sub-category-consents?tenant_id=${tenantId}${parentQuery}`;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(url, { headers });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.detail || body?.message || 'Failed to load multilingual consent');
      const source = Array.isArray(body) ? body : (body?.rows || []);
      const variants = source
        .map((item) => {
          let parsed = {};
          try {
            const raw = item?.data ?? item?.parsed ?? {};
            parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw && typeof raw === 'object' ? raw : {});
          } catch (e) {
            parsed = {};
          }
          return {
            ...item,
            parsed,
            version: item.version || parsed.version || parsed.consent_version || '',
            status: item.status || parsed.status || 'under_process',
            locked: Boolean(item.locked || parsed.locked || String(item.status || parsed.status || '').toLowerCase() === 'accepted'),
          };
        })
        .filter((item) => {
          const parsed = item?.parsed || {};
          const matchesKind = parsed.kind === 'multilingual_consent'
            || parsed.is_multilingual_variant
            || item.kind === 'multilingual_consent'
            || item.parent_sub_consent_id != null;
          const storedParentId = item.parent_sub_consent_id
            ?? parsed.parent_sub_consent_id
            ?? parsed.parentSubConsentId
            ?? '';
          return matchesKind && String(storedParentId) === String(parentSubConsentId);
        });
      setRows(variants);
    } catch (e) {
      setError(e.message || 'Failed to load multilingual consent');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [parentSubConsentId, tenantId, token]);

  useEffect(() => {
    if (tenantId) fetchRows();
  }, [fetchRows, tenantId]);

  const resetModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setTitle('');
    setMessage('');
    setLanguageCode('hi');
  };

  const openCreateModal = () => {
    resetModal();
    setShowModal(true);
  };

  const openEditModal = (item) => {
    const parsed = item?.parsed || {};
    setEditingItem(item);

    // setTitle(item?.sub_category || item?.name || '');
    // setLanguageCode(parsed.language_code || 'hi');
    // setMessage(item?.message || parsed.consent_message || '');
    const initialTitle = parsed.title || item?.title || item?.sub_category || item?.name || item?.consent_type || '';
    setTitle(initialTitle);
    setLanguageCode(parsed.language_code || item?.language_code || 'hi');
    setMessage(item?.consent_message || item?.message || parsed.consent_message || '');
    setShowModal(true);
  };

  const openViewModal = (item) => {
    setViewingItem(item);
  };

  const closeViewModal = () => {
    setViewingItem(null);
  };

  const openDeleteConfirm = (item) => {
    setDeletingItem(item);
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setDeletingItem(null);
  };

  const acceptVersion = async (item) => {
    const confirmed = await Swal.fire({ title: 'Accept & Lock language version?', text: 'This version cannot be edited after acceptance.', icon: 'question', showCancelButton: true, confirmButtonText: 'Accept & Lock', confirmButtonColor: '#059669' });
    if (!confirmed.isConfirmed) return;
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API_BASE}/manager/accept-subcategory-version`, { method: 'POST', headers, body: JSON.stringify({ id: item.id, tenant_id: tenantId }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || 'Failed to accept language version');
      await fetchRows();
      await Swal.fire({ icon: 'success', title: 'Accepted & Locked', timer: 1600, showConfirmButton: false });
    } catch (e) { await Swal.fire({ icon: 'error', title: 'Accept Failed', text: e.message }); }
  };

  const showDuplicateAlert = async (titleDuplicate, languageDuplicate) => {
    if (titleDuplicate && languageDuplicate) {
      await Swal.fire({ icon: 'warning', title: 'Duplicate consent', text: 'This consent title and language already exist for this sub-consent.' });
    } else if (titleDuplicate) {
      await Swal.fire({ icon: 'warning', title: 'Duplicate consent title', text: 'This consent title already exists for this sub-consent.' });
    } else if (languageDuplicate) {
      await Swal.fire({ icon: 'warning', title: 'Duplicate language', text: 'This language already exists for this sub-consent.' });
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    try {
      setDeleting(true);
      setError('');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const url = `${API_BASE}/manager/sub-category-consents/${deletingItem.id}?tenant_id=${encodeURIComponent(tenantId)}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || body?.message || 'Failed to delete language version');
      setDeletingItem(null);
      await fetchRows();
    } catch (e) {
      setError(e.message || 'Failed to delete language version');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Please enter a consent name'); return; }
    if (!message.trim()) { setError('Please enter consent content'); return; }
    const normalizedLanguage = String(languageCode || '').trim().toLowerCase();
    if (parentSubConsentId == null || parentConsentId == null) {
      setError('This sub-consent is missing its parent relationship. Refresh the list or repair the sub-consent mapping before adding a language.');
      return;
    }
    const duplicateLanguage = rows.some((item) => {
      if (editingItem && String(item.id) === String(editingItem.id)) return false;
      const parsed = item?.parsed || {};
      const existingLanguage = parsed.language_code || item?.language_code || parsed.language || item?.language_name || '';
      const normalizedExistingLanguage = LANGUAGE_OPTIONS.find(
        (option) => option.label.toLowerCase() === String(existingLanguage).trim().toLowerCase(),
      )?.value || String(existingLanguage).trim().toLowerCase();
      return normalizedExistingLanguage === normalizedLanguage;
    });
    if (duplicateLanguage) {
      setError('');
      await showDuplicateAlert(false, true);
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const currentVersion = parentVersion;
      const payload = {
        sub_category: parentSubConsentName || title.trim(),
        name: parentKey,
        consent_type: parentKey,
        parent_consent_type: parentKey,
        parent_consent_label: parentLabel,
        parent_sub_consent_id: parentSubConsentId,
        parent_consent_id: parentConsentId,
        consent_id: parentConsentId,
        sub_consent_id: parentSubConsentId,
        message,
        data: JSON.stringify(buildMultilingualVariantPayload({
          parentType: parentKey,
          parentLabel,
          languageLabel: LANGUAGE_OPTIONS.find(item => item.value === languageCode)?.label || languageCode,
          languageCode,
          consentMessage: message,
          title,
          parentSubConsentId,
          parentConsentId,
          subConsentId: parentSubConsentId,
          version: currentVersion,
          color: '#7c3aed',
        })),
        tenant_id: tenantId,
        version: currentVersion,
      };
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const url = editingItem
        ? `${API_BASE}/manager/update-subcategory`
        : `${API_BASE}/manager/create-subcategory`;
      const reqPayload = editingItem ? { ...payload, id: editingItem.id } : payload;
      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(reqPayload) });
      const body = await res.json().catch(() => ({}));
      const detail = typeof body?.detail === 'string'
        ? body.detail
        : typeof body?.message === 'string'
          ? body.message
          : '';
      if (!res.ok && /duplicate|already exists|language already exists/i.test(detail)) {
        setError('');
        await showDuplicateAlert(false, true);
        return;
      }
      if (!res.ok) throw new Error(body?.detail || body?.message || 'Failed to save multilingual consent');
      resetModal();
      await fetchRows();
      await Swal.fire({
        icon: 'success',
        title: editingItem ? 'Language version updated' : 'Language version created',
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (e) {
      const message = e.message || 'Failed to save multilingual consent';
      setError(message);
      await Swal.fire({ icon: 'error', title: 'Save Failed', text: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Language Versions • {parentSubConsentName || parentLabel}</h2>
          <p style={styles.subTitle}>Create and check language versions of one consent in one place.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.7rem' }}>
          <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={openCreateModal} disabled={parentSubConsentId == null || parentConsentId == null} title={parentSubConsentId == null || parentConsentId == null ? 'Refresh the parent consent and sub-consent list to load stable IDs' : 'Add a language version'}>
            <PlusCircle size={16} style={{ marginRight: 6 }} />Add Language
          </button>
          <button style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => onBack && onBack()}>
            <ArrowLeft size={16} style={{ marginRight: 6 }} />Back
          </button>
        </div>
      </div>

      {error ? <div style={{ marginBottom: '0.8rem', padding: '0.8rem', background: '#fee2e2', color: '#b91c1c', borderRadius: '10px' }}>{error}</div> : null}

      {loading ? <div style={styles.empty}>Loading language variants…</div> : null}

      {!loading && rows.length === 0 ? (
        <div style={styles.empty}>No multilingual versions yet. Add one to start.</div>
      ) : null}

      <div style={styles.cardGrid}>
        {rows.map((item) => {
          const parsed = item.parsed || {};
          return (
            <div key={item.id} style={{ ...styles.card, cursor: 'default' }}>
              <div style={styles.cardHeader}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.badge}><Languages size={14} />{parsed.language || parsed.language_name || item.language_name || 'Unknown'}</div>
                  <h4 style={styles.cardTitle}>{parsed.title || item.sub_category || item.name || parentLabel}</h4>
                </div>
                <div style={styles.iconBtnRow}>
                  <button
                    style={{ ...styles.iconBtn, ...styles.iconBtnView }}
                    onClick={() => openViewModal(item)}
                    title="View"
                    aria-label="View language version"
                  >
                    <Eye size={15} />
                  </button>
                  {!(item.locked || String(item.status || '').toLowerCase() === 'accepted') && <>
                    <button style={{ ...styles.iconBtn, ...styles.iconBtnEdit }} onClick={() => openEditModal(item)} title="Edit" aria-label="Edit language version"><Edit size={15} /></button>
                    <button style={{ ...styles.iconBtn, ...styles.iconBtnDelete }} onClick={() => openDeleteConfirm(item)} title="Delete" aria-label="Delete language version"><Trash2 size={15} /></button>
                  </>}
                  <button
                    style={{ ...styles.iconBtn, color: (item.locked || String(item.status || '').toLowerCase() === 'accepted') ? '#64748b' : '#059669', opacity: (item.locked || String(item.status || '').toLowerCase() === 'accepted') ? 0.65 : 1 }}
                    onClick={() => acceptVersion(item)}
                    disabled={Boolean(item.locked || String(item.status || '').toLowerCase() === 'accepted')}
                    title={(item.locked || String(item.status || '').toLowerCase() === 'accepted') ? 'Accepted and locked' : 'Accept & Lock'}
                    aria-label="Accept and lock"
                  >
                    {(item.locked || String(item.status || '').toLowerCase() === 'accepted') ? <Lock size={15} /> : <CheckCircle size={15} />}
                  </button>
                </div>
              </div>
              <div style={styles.cardMeta}>Parent consent: {parsed.parent_consent_label || parentLabel}</div>
              <div style={styles.contentPreview}>{item.message || parsed.consent_message || 'No content yet'}</div>
              <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Language code: {parsed.language_code || '—'}</div>
              <div style={{ color: '#2563eb', fontSize: '0.85rem', fontWeight: 700, marginTop: 6 }}>Version: v{item.version || '—'}</div>
              <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: 5 }}>Created: {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</div>
              {item.updated_at ? <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: 3 }}>Updated: {new Date(item.updated_at).toLocaleString()}</div> : null}
              <div style={{ color: item.locked ? '#15803d' : '#b45309', fontSize: '0.8rem', fontWeight: 700, marginTop: '0.4rem' }}>{item.locked ? '🔒 ACCEPTED & LOCKED' : '✏️ UNDER PROCESS'}</div>
            </div>
          );
        })}
      </div>
      {showModal ? (
        <div style={styles.modalOverlay} onClick={resetModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{editingItem ? 'Edit Language Consent' : 'Create New Language Consent'}</h3>
            <label style={{ fontSize: '0.9rem', fontWeight: 600, display: 'block', marginBottom: '0.3rem' }}>Consent Name</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Bank Consent Hindi" style={styles.input} />
            <label style={{ fontSize: '0.9rem', fontWeight: 600, display: 'block', marginBottom: '0.3rem' }}>Language</label>
            <select value={languageCode} onChange={(e) => setLanguageCode(e.target.value)} style={styles.input}>
              {LANGUAGE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <label style={{ fontSize: '0.9rem', fontWeight: 600, display: 'block', marginBottom: '0.3rem' }}>Consent Content</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Enter consent content for this language..." style={styles.textarea} />
            <label style={{ fontSize: '0.9rem', fontWeight: 600, display: 'block', margin: '0.8rem 0 0.3rem' }}>Version</label>
            <input value={parentVersion} readOnly disabled aria-label="Parent sub-consent version" title="Version comes from the sub-consent" style={{ ...styles.input, background: '#f1f5f9', color: '#475569', cursor: 'not-allowed' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.7rem', marginTop: '0.9rem' }}>
              <button style={{ ...styles.btn, background: '#fff', color: '#64748b', border: '1px solid #e2e8f0' }} onClick={resetModal}>Cancel</button>
              <button style={{ ...styles.btn, background: '#2563eb', color: '#fff' }} onClick={handleSave} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      ) : null}

      {viewingItem ? (
        <div style={styles.modalOverlay} onClick={closeViewModal}>
          <div style={styles.viewModal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
              <h3 style={{ margin: 0 }}>{viewingItem.parsed?.title || viewingItem.sub_category || viewingItem.name || parentLabel}</h3>
              <div style={styles.badge}><Languages size={14} />{viewingItem.parsed?.language || 'Unknown'}</div>
            </div>
            <div style={styles.viewContent}>
              <div style={styles.viewLabel}>Parent Consent</div>
              <div style={styles.viewValue}>{viewingItem.parsed?.parent_consent_label || parentLabel}</div>
              <div style={styles.viewLabel}>Language Code</div>
              <div style={styles.viewValue}>{viewingItem.parsed?.language_code || '—'}</div>
              <div style={styles.viewLabel}>Consent Content</div>
              <div style={styles.viewValue}>{viewingItem.message || viewingItem.parsed?.consent_message || 'No content yet'}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.7rem', marginTop: '0.9rem', flexShrink: 0 }}>
              <button style={{ ...styles.btn, background: '#fff', color: '#64748b', border: '1px solid #e2e8f0' }} onClick={closeViewModal}>Close</button>
              {!(viewingItem.locked || String(viewingItem.status || '').toLowerCase() === 'accepted') && <button style={{ ...styles.btn, background: '#2563eb', color: '#fff' }} onClick={() => {
                closeViewModal();
                openEditModal(viewingItem);
              }}><Edit size={14} style={{ marginRight: 6 }} />Edit</button>}
            </div>
          </div>
        </div>
      ) : null}

      {deletingItem ? (
        <div style={styles.modalOverlay} onClick={closeDeleteConfirm}>
          <div style={{ ...styles.modal, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Delete Language Version</h3>
            <p style={{ color: '#475569', fontSize: '0.95rem' }}>
              Are you sure you want to delete "{deletingItem.sub_category || deletingItem.name}"? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.7rem', marginTop: '0.9rem' }}>
              <button style={{ ...styles.btn, background: '#fff', color: '#64748b', border: '1px solid #e2e8f0' }} onClick={closeDeleteConfirm} disabled={deleting}>Cancel</button>
              <button style={{ ...styles.btn, background: '#dc2626', color: '#fff' }} onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default SubMultilingualConsentPage;